import { NIL_UUID } from '../common/uuid.types.js'

/**
 * Centralized enum for all application event types.
 * These event types are used across EventBridge, SQS queues, and the events log table.
 *
 * @remarks
 * - Only add events that are actually implemented
 * - Event types follow the pattern: `{entity}.{action}`
 * - Use lowercase with underscores for consistency with EventBridge
 *
 * Every member is persisted by `event-service`'s audit-log sink (it matches all
 * `app.*` events). A new member therefore also needs a payload classification
 * row in `docs/compliance/audit-log-pii-review.md` — `npm run gate:audit-pii`
 * fails CI without one (TO-446). To keep a type out of the audit log, add it to
 * `AUDIT_LOG_EXCLUDED_EVENT_TYPES` in event-service's
 * `infra/audit-log-sink.constants.ts` and classify it `excluded`.
 */
export enum EventType {
	// ─── User Events ──────────────────────────────────────────────────────────
	USER_LOGGED_IN = 'user.logged_in',
	// Emitted by the provisioning path when a Cognito identity and its `users`
	// row are created together. The audit record for TO-303 REQ-001/REQ-002.
	USER_PROVISIONED = 'user.provisioned',
	// Emitted by the tenant-admin surface (TO-307) after a user's role set is
	// changed / after a user is deactivated. The audit records for REQ-004/REQ-005.
	USER_ROLES_CHANGED = 'user.roles_changed',
	USER_DEACTIVATED = 'user.deactivated',
	// A tenant admin revoked a user's sessions (TO-64): Cognito global sign-out
	// plus the `_auth` revocation instant. The audit record for REQ-005.
	USER_SESSIONS_REVOKED = 'user.sessions_revoked',
	// A tenant admin set or cleared a user's own MFA mandate (TO-593). Ids and the
	// new boolean only. The audit record for configurable-mfa-mandate REQ-003.
	USER_MFA_MANDATE_CHANGED = 'user.mfa_mandate_changed',
	// Invitation lifecycle + first-login activation (TO-309). Ids and enums
	// only — never the link token, its hash, or the invitation URL.
	USER_INVITATION_ISSUED = 'user.invitation_issued',
	USER_INVITATION_REVOKED = 'user.invitation_revoked',
	USER_ACTIVATED = 'user.activated',
	// TO-591 first-login MFA: a native first-login password-set completed
	// (federated has no password), and a user confirmed a TOTP factor. The
	// machine-consumable signals alongside the existing `MFA_ENROLLED` audit log.
	USER_PASSWORD_CHANGED = 'user.password_changed',
	USER_MFA_ENROLLED = 'user.mfa_enrolled',
	// A live user's display name or email changed (TO-426) — full directory
	// state, for consumer projections; also the user snapshot of
	// `directory:republish`.
	USER_UPDATED = 'user.updated',
	// Personal API-key lifecycle (TO-516): published inside the write's
	// transaction, so no key is minted or revoked unaudited. Ids, prefix and
	// enums only — never the plaintext, the hash or the free-text name.
	API_KEY_CREATED = 'api_key.created',
	API_KEY_REVOKED = 'api_key.revoked',

	// ─── Tenant Events ────────────────────────────────────────────────────────
	// The super-admin tenant lifecycle's audit record (TO-306): create (with its
	// Cognito group + first admin), any name/slug/status change, soft-delete.
	TENANT_CREATED = 'tenant.created',
	TENANT_UPDATED = 'tenant.updated',
	TENANT_DELETED = 'tenant.deleted',
	/** A tenant's SSO (SAML IdP) configuration or federation status changed (TO-308). */
	TENANT_IDP_UPDATED = 'tenant.idp_updated',
	/** A tenant configuration console section was saved (TO-374): actor, section, before and after. */
	TENANT_CONFIG_UPDATED = 'tenant.config_updated',
	/**
	 * A tenant's content-locale settings were replaced (TO-415). Carries the full
	 * settings; product-service projects it into its own table (ADR-0030).
	 */
	TENANT_LOCALE_SETTINGS_UPDATED = 'tenant.locale_settings_updated',
	/**
	 * The seeding contract (TO-375): a bounded context's seeder reports the
	 * outcome of seeding a tenant after `tenant.created`. One of the two per run.
	 * Each context is discriminated by `data.context` (content-service, TO-419).
	 */
	TENANT_SEED_COMPLETED = 'tenant.seed_completed',
	TENANT_SEED_FAILED = 'tenant.seed_failed',

	// ─── Platform Events ──────────────────────────────────────────────────────
	/**
	 * A super-admin switched the runtime platform-brand pointer (TO-541). Brand
	 * ids only; never emitted for a no-op re-selection or a refused request.
	 */
	PLATFORM_BRAND_UPDATED = 'platform.brand_updated',

	// ─── Auth Events ──────────────────────────────────────────────────────────
	// Emitted when an admin on a non-prod stage mints an impersonation JWT for a
	// target user via POST /auth/dev/impersonate. See ADR-0009.
	AUTH_IMPERSONATION_STARTED = 'auth.impersonation_started',

	// ─── Notification Events ──────────────────────────────────────────────────
	NOTIFICATION_EMAIL_REQUESTED = 'notification.email_requested',
	NOTIFICATION_EMAIL_SENT = 'notification.email_sent',
	NOTIFICATION_EMAIL_FAILED = 'notification.email_failed',
	NOTIFICATION_SMS_REQUESTED = 'notification.sms_requested',
	NOTIFICATION_SMS_SENT = 'notification.sms_sent',
	NOTIFICATION_SMS_FAILED = 'notification.sms_failed',
	NOTIFICATION_SMS_OPT_OUT_RECEIVED = 'notification.sms_opt_out_received',
	NOTIFICATION_SMS_OPT_IN_RECEIVED = 'notification.sms_opt_in_received',
	/** A tenant's SMS settings were saved (TO-383) — an audit record; never carries a credential. */
	NOTIFICATION_SMS_SETTINGS_UPDATED = 'notification.sms_settings_updated',
	// SES feedback + email engagement (TO-136). Envelopes carry the real tenant;
	// payloads carry ids, enums and timestamps only — never an address or URL.
	NOTIFICATION_EMAIL_DELIVERED = 'notification.email_delivered',
	NOTIFICATION_EMAIL_BOUNCED = 'notification.email_bounced',
	NOTIFICATION_EMAIL_COMPLAINED = 'notification.email_complained',
	NOTIFICATION_EMAIL_OPENED = 'notification.email_opened',
	NOTIFICATION_EMAIL_CLICKED = 'notification.email_clicked',

	// ─── Customer Events ──────────────────────────────────────────────────────
	CUSTOMER_CREATED = 'customer.created',
	CUSTOMER_UPDATED = 'customer.updated',
	CUSTOMER_DELETED = 'customer.deleted',
	// Customer model v2 (TO-418). Payloads carry ids, enums and field/key names
	// only — never names, emails, phone numbers, note bodies or attribute values.
	CUSTOMER_REASSIGNED = 'customer.reassigned',
	CUSTOMER_CONVERTED = 'customer.converted',
	CUSTOMER_STATUS_CHANGED = 'customer.status_changed',
	CUSTOMER_CONTACT_POINT_ADDED = 'customer.contact_point_added',
	CUSTOMER_CONTACT_POINT_UPDATED = 'customer.contact_point_updated',
	CUSTOMER_CONTACT_POINT_REMOVED = 'customer.contact_point_removed',
	CUSTOMER_CONSENT_RECORDED = 'customer.consent_recorded',
	CUSTOMER_NOTE_ADDED = 'customer.note_added',
	CUSTOMER_ATTRIBUTES_CHANGED = 'customer.attributes_changed',
	CUSTOMER_ANONYMIZED = 'customer.anonymized',
	/** A customer contracted a product — a hard conversion (TO-405). */
	CUSTOMER_PRODUCT_CONTRACTED = 'customer.product_contracted',

	// ─── Compliance Events (TO-232, ADR-0057) ─────────────────────────────────
	// GDPR erasure + subject access fan-out. Ids, enums, timestamps, object keys
	// and integer counts only — never a contact value, name, note, hold reason
	// or record content (subject data travels in the S3 subject manifest).
	ERASURE_REQUESTED = 'erasure.requested',
	ERASURE_STEP_REQUESTED = 'erasure.step_requested',
	ERASURE_STEP_REPORTED = 'erasure.step_reported',
	ERASURE_CONFIRMED = 'erasure.confirmed',
	ERASURE_CANCELLED = 'erasure.cancelled',
	ERASURE_COMPLETED = 'erasure.completed',
	LEGAL_HOLD_PLACED = 'legal_hold.placed',
	LEGAL_HOLD_RELEASED = 'legal_hold.released',
	SAR_REQUESTED = 'sar.requested',
	SAR_SECTION_REPORTED = 'sar.section_reported',
	SAR_BUNDLE_READY = 'sar.bundle_ready',
	SAR_BUNDLE_DOWNLOADED = 'sar.bundle_downloaded',

	// ─── Product Events ───────────────────────────────────────────────────────
	PRODUCT_CREATED = 'product.created',
	PRODUCT_UPDATED = 'product.updated',
	PRODUCT_DELETED = 'product.deleted',
	// Product catalog v2 (TO-415): the audited lifecycle, categories, translations.
	PRODUCT_STATUS_CHANGED = 'product.status_changed',
	PRODUCT_CATEGORY_CREATED = 'product.category_created',
	PRODUCT_CATEGORY_UPDATED = 'product.category_updated',
	PRODUCT_CATEGORY_DELETED = 'product.category_deleted',
	PRODUCT_TRANSLATION_UPSERTED = 'product.translation_upserted',
	PRODUCT_TRANSLATION_DELETED = 'product.translation_deleted',

	// ─── File Events ──────────────────────────────────────────────────────────
	// file-service's HTTP router emits the first five; its processing handler
	// emits the last two. `file.uploaded` and `file.reprocess_requested` are the
	// two the processing queue subscribes to (TO-324).
	FILE_UPLOADED = 'file.uploaded',
	FILE_DOWNLOADED = 'file.downloaded',
	FILE_UPDATED = 'file.updated',
	FILE_DELETED = 'file.deleted',
	FILE_REPROCESS_REQUESTED = 'file.reprocess_requested',
	FILE_PROCESSING_COMPLETED = 'file.processing_completed',
	FILE_PROCESSING_FAILED = 'file.processing_failed',
	// TO-443: a file entered QUARANTINED (content check or malware verdict),
	// and a tenant branding image was rasterized into the public bucket. Neither
	// is subscribed by the processing queue.
	FILE_QUARANTINED = 'file.quarantined',
	FILE_BRANDING_ASSET_UPLOADED = 'file.branding_asset_uploaded',

	// ─── AI Task Events ───────────────────────────────────────────────────────
	// A service asks `ai-service` to run an LLM task by naming a use case
	// from the router's registry; the worker reports the outcome. The router owns
	// no table and never imports the asking service — these three events are its
	// entire input and output surface. See the `ai-task-routing` capability.
	AI_TASK_REQUESTED = 'ai.task_requested',
	AI_TASK_COMPLETED = 'ai.task_completed',
	AI_TASK_FAILED = 'ai.task_failed',

	// ─── Offer PDF Events ─────────────────────────────────────────────────────
	// The PDF worker reports the outcome of rendering an offer's PDF. Since TO-339
	// the PDF is produced by consuming OFFER_ARTIFACT_REQUESTED (shared renderId) —
	// there is no separate OFFER_PDF_REQUESTED trigger. See ADR-0015.
	OFFER_PDF_GENERATED = 'offer.pdf_generated',
	OFFER_PDF_FAILED = 'offer.pdf_failed',

	// ─── Prompt Library Events (TO-327) ─────────────────────────────────────────
	// ai-service is the single source of every LLM prompt (ADR-0039 rule 4).
	// The head lifecycle and its immutable published versions, reusable lists,
	// per-tenant config, test cases, and evaluation runs each emit an audit event.
	PROMPT_CREATED = 'prompt.created',
	PROMPT_UPDATED = 'prompt.updated',
	PROMPT_ARCHIVED = 'prompt.archived',
	PROMPT_DELETED = 'prompt.deleted',
	PROMPT_PUBLISHED = 'prompt.published',
	PROMPT_LABEL_MOVED = 'prompt.label_moved',
	PROMPT_VARIABLE_LIST_CREATED = 'prompt.variable_list_created',
	PROMPT_VARIABLE_LIST_UPDATED = 'prompt.variable_list_updated',
	PROMPT_VARIABLE_LIST_DELETED = 'prompt.variable_list_deleted',
	PROMPT_DEFAULT_CONFIG_UPDATED = 'prompt.default_config_updated',
	PROMPT_TENANT_SETTINGS_UPDATED = 'prompt.tenant_settings_updated',
	PROMPT_TEST_CASE_CREATED = 'prompt.test_case_created',
	PROMPT_TEST_CASE_UPDATED = 'prompt.test_case_updated',
	PROMPT_TEST_CASE_DELETED = 'prompt.test_case_deleted',
	// The evaluation run's own lifecycle. Dispatch of each case reuses the
	// existing ai.task_requested; these three bracket the run as a whole.
	PROMPT_EVAL_REQUESTED = 'prompt.eval_requested',
	PROMPT_EVAL_COMPLETED = 'prompt.eval_completed',
	PROMPT_EVAL_FAILED = 'prompt.eval_failed',

	// ─── Offer Landing Render Events ──────────────────────────────────────────
	// An offer was sent (the shrunk announcement — identifiers only). The recipient
	// opened the offer: the landing BFF served its page at GET /l/{landing_id}
	// (TO-280/TO-339). See ADR-0015 + the offer-events payload types.
	OFFER_SENT = 'offer.sent',
	OFFER_OPENED = 'offer.opened',
	// The recipient clicked the landing's CTA — recorded by tracking-service's
	// ingest worker from the landing beacon (TO-165), which also emits the
	// client-confirmed OFFER_OPENED (carrying a sessionId).
	OFFER_CTA_CLICKED = 'offer.cta_clicked',
	// ─── Offer aggregate + orchestration (TO-339) ────────────────────────────
	// offer-service owns the offer entity and orchestrates artifact generation.
	// On create it emits OFFER_CREATED (the fact); on send / re-version (TO-464)
	// OFFER_ARTIFACT_REQUESTED (ONE event both the landing and PDF workers read,
	// sharing a renderId — the fix for the renderId mismatch). The landing worker announces OFFER_LANDING_GENERATED
	// (the TO-339 replacement for the legacy offer.rendered).
	OFFER_CREATED = 'offer.created',
	OFFER_ARTIFACT_REQUESTED = 'offer.artifact_requested',
	OFFER_LANDING_GENERATED = 'offer.landing_generated',
	// ─── Render-at-send + re-versioning (TO-464) ─────────────────────────────
	// Since TO-464 OFFER_ARTIFACT_REQUESTED is emitted at send / re-version, not at
	// create. The landing worker reports a failed attempt with OFFER_LANDING_FAILED
	// (the PDF worker already emits OFFER_PDF_FAILED); offer-service consumes both.
	// A completed re-version of a SENT offer publishes OFFER_UPDATED.
	OFFER_LANDING_FAILED = 'offer.landing_failed',
	OFFER_UPDATED = 'offer.updated',

	// ─── Inbound Webhook Events ───────────────────────────────────────────────
	// An outside system POSTed a delivery, its signature was proven against the
	// receiving endpoint's secret, and its verbatim bytes were stored. Carries
	// references only — never the body or anything read out of it, because the
	// ledger is agnostic about payload shape (TO-323).
	WEBHOOK_RECEIVED = 'webhook.received',
	// ─── Outbound Webhook Events (TO-381) ─────────────────────────────────────
	// A subscription's fifth consecutive failed delivery switched it off; the
	// notification-worker emails the tenant's org admins. Carries the target host
	// only — never the URL path/query or any secret.
	WEBHOOK_SUBSCRIPTION_AUTO_DISABLED = 'webhook.subscription_auto_disabled',
	// A tenant administrator generated or rotated the outbound signing secret.
	// Audit only: names the actor, never the secret.
	WEBHOOK_SIGNING_SECRET_ROTATED = 'webhook.signing_secret_rotated',
	// ─── Short URL Events ─────────────────────────────────────────────────────
	// short-url-service mints a code for a target (created) and the public
	// GET /s/{code} redirect resolves one (resolved — i.e. the click). The
	// tracking surface for the shortener (TO-281).
	SHORT_URL_CREATED = 'short_url.created',
	SHORT_URL_RESOLVED = 'short_url.resolved',

	// ─── Organization Events (TO-411) ─────────────────────────────────────────
	// organization-service owns the tenant's organizational units. Consumers
	// (assignments, scoped authorization, importers) react to these instead of
	// reading its tables (ADR-0030).
	ORG_UNIT_CREATED = 'org_unit.created',
	ORG_UNIT_UPDATED = 'org_unit.updated',
	ORG_UNIT_DEACTIVATED = 'org_unit.deactivated',
	ORG_UNIT_MOVED = 'org_unit.moved',
	// Physical locations of units (TO-387). Each carries the full location so a
	// render-side consumer can keep its own read model (O6, TO-425).
	ORGANIZATION_LOCATION_CREATED = 'organization_location.created',
	ORGANIZATION_LOCATION_UPDATED = 'organization_location.updated',
	ORGANIZATION_LOCATION_DELETED = 'organization_location.deleted',
	// User ↔ unit assignments (TO-412). `primary_changed` fires whenever a user's
	// current primary unit changes, so a consumer resolving "the user's branch"
	// subscribes to that one event.
	ORG_ASSIGNMENT_CREATED = 'org_assignment.created',
	ORG_ASSIGNMENT_ENDED = 'org_assignment.ended',
	ORG_ASSIGNMENT_PRIMARY_CHANGED = 'org_assignment.primary_changed',

	// ─── Content Events (TO-419) ──────────────────────────────────────────────
	// content-service owns versioned, approvable texts and their assignments.
	// Payloads carry ids, codes, locale and version only — never title/body/reason.
	CONTENT_TYPE_CREATED = 'content.type_created',
	CONTENT_TYPE_UPDATED = 'content.type_updated',
	CONTENT_TEXT_CREATED = 'content.text_created',
	CONTENT_TEXT_ARCHIVED = 'content.text_archived',
	CONTENT_VERSION_CREATED = 'content.version_created',
	CONTENT_LOCALIZATION_SUBMITTED = 'content.localization_submitted',
	CONTENT_LOCALIZATION_APPROVED = 'content.localization_approved',
	CONTENT_LOCALIZATION_REJECTED = 'content.localization_rejected',
	CONTENT_LOCALIZATION_PUBLISHED = 'content.localization_published',
	CONTENT_LOCALIZATION_RETIRED = 'content.localization_retired',
	CONTENT_LOCALIZATION_EXPIRING = 'content.localization_expiring',
	CONTENT_ASSIGNMENT_CREATED = 'content.assignment_created',
	CONTENT_ASSIGNMENT_UPDATED = 'content.assignment_updated',
	CONTENT_ASSIGNMENT_REMOVED = 'content.assignment_removed',

	// ─── File Exchange Events (TO-391) ────────────────────────────────────────
	// A tenant's logical file configuration changed: actor, version, the changed
	// fields, before and after. The configuration holds no credential.
	FILE_EXCHANGE_FILE_CONFIG_UPDATED = 'file_exchange.file_config_updated',
	// An import execution passed its malware scan and is ready to import
	// (TO-413; catalog alias `FileReadyForImport`). Identifiers only.
	FILE_EXCHANGE_IMPORT_READY = 'file_exchange.import_ready',
	// A received file's (tenant, logical file, sha256) already had an execution;
	// the new arrival was dropped (TO-413). Identifiers only.
	FILE_EXCHANGE_IMPORT_DUPLICATE_DETECTED = 'file_exchange.import_duplicate_detected',
	// An import-definition version moved DRAFT → ACTIVE and is immutable from now
	// on (TO-414). Key, version and canonical schema only — not the contract.
	FILE_EXCHANGE_IMPORT_DEFINITION_ACTIVATED = 'file_exchange.import_definition_activated',
	// An import-definition version moved ACTIVE → RETIRED: still readable, no
	// longer usable by new imports (TO-414).
	FILE_EXCHANGE_IMPORT_DEFINITION_RETIRED = 'file_exchange.import_definition_retired',
	// A scheduled logical file was not received by its deadline; one per
	// (tenant, logical file, due date), delivered at least once (TO-398).
	FILE_EXCHANGE_FILE_MISSING = 'file_exchange.file_missing',
	// The malware scan rejected an import execution as infected (TO-398).
	FILE_EXCHANGE_IMPORT_SECURITY_REJECTED = 'file_exchange.import_security_rejected',
	// An import execution failed parsing, transformation or validation (TO-398
	// contract; F6–F8 emit it).
	FILE_EXCHANGE_IMPORT_INVALID = 'file_exchange.import_invalid',
	// An import completed with rejected records (TO-398 contract; F9 emits it).
	FILE_EXCHANGE_IMPORT_COMPLETED_WITH_ERRORS = 'file_exchange.import_completed_with_errors',
	// A user obtained a download link to an import's original file (TO-399): the
	// audit trail of every raw-file access — identifiers only, never content.
	FILE_EXCHANGE_IMPORT_ORIGINAL_FILE_DOWNLOADED = 'file_exchange.import_original_file_downloaded',
	// A Super-Admin created a tenant's SFTP connector (TO-393): name, host, port
	// and the public-key fingerprint — never the key, secret or AWS connector id.
	FILE_EXCHANGE_CONNECTOR_CREATED = 'file_exchange.connector_created',
	// A Super-Admin changed a connector: the changed field names and whether the
	// host-key trust was cleared (TO-393).
	FILE_EXCHANGE_CONNECTOR_UPDATED = 'file_exchange.connector_updated',
	// A connector's server host key became trusted, confirmed or pinned (TO-393).
	FILE_EXCHANGE_CONNECTOR_HOST_KEY_TRUSTED = 'file_exchange.connector_host_key_trusted',
	// A Super-Admin deleted a connector no logical file used (TO-393).
	FILE_EXCHANGE_CONNECTOR_DELETED = 'file_exchange.connector_deleted',

	// ─── Realtime Events (TO-468) ─────────────────────────────────────────────
	// An operator's end-to-end probe of the realtime push channel: emitted by
	// `scripts/src/local/emit-realtime-test-event.ts`, pushed by notification-worker
	// to the named user's realtime topic. Carries the user as entityRef plus an
	// optional operator note — no personal data.
	REALTIME_DIAGNOSTIC_PING = 'realtime.diagnostic_ping',
}

/**
 * Who emitted an event.
 *
 * Every `I*Event` envelope carries a `source`. Historically this was the string
 * union `'api' | 'worker'` inline on each envelope; this enum is the single
 * vocabulary those envelopes should reference so a producer names a member
 * rather than re-typing a literal. Applied to the `ai.*` envelope first (TO-326);
 * rolling it across the other envelopes is tracked platform-wide in TO-344.
 *
 * @remarks
 * - `API` — a request-handling service emitted it (a request came in and this
 *   is the event that request produced).
 * - `WORKER` — an async worker emitted it while reporting the outcome of work.
 * - `OPERATOR` — an operator-run script emitted it (today the realtime
 *   diagnostic ping, TO-468), not a request or a worker.
 * - `SCIM` — a tenant's IdP emitted it over the SCIM provisioning surface
 *   (TO-379); the request was authenticated by the per-tenant SCIM token, not a
 *   user principal.
 * - `FILE_IMPORT` — a domain importer emitted it while applying a tenant's
 *   imported file (TO-401); there is no user principal.
 */
export enum EventSource {
	API = 'api',
	WORKER = 'worker',
	OPERATOR = 'operator',
	SCIM = 'scim',
	FILE_IMPORT = 'file-import',
}

/**
 * The envelope schema version an event was produced against.
 *
 * A single member today (`V1_0 = '1.0'`); a breaking change to any envelope's
 * shape adds a member here rather than silently changing what `'1.0'` means, so
 * a consumer can branch on the version it knows how to read. Kept a string
 * (semver-shaped: `'1.0'`, later `'1.1'`/`'2.0'`), not a number — `1.10` and
 * `1.1` are distinct versions but equal numbers. Applied to the `ai.*` envelope
 * first (TO-326); platform-wide rollout tracked with {@link EventSource}.
 */
export enum EventVersion {
	V1_0 = '1.0',
}

/**
 * Reserved placeholder tenant for events whose producer has no tenant context
 * at emit time — today `notification.*` (the worker runs off SQS with no auth
 * context) and `auth.impersonation_started` (a super-admin act that crosses
 * tenants by definition).
 *
 * Every `I*Event` envelope requires `tenantId`, so those producers need a value.
 * This is the {@link NIL_UUID}: syntactically valid (so `event-service` persists
 * it verbatim rather than coercing it to `null`) but can never be a real
 * `tenants.tenant_id` (migration 0015's `tenants_tenant_id_not_reserved` CHECK
 * bars it). Audit reads filter on the caller's DB-resolved tenant, so a row
 * stamped with it is never returned to anyone — the same isolation the old
 * nullable column gave, now reachable through a required field.
 *
 * **Temporary.** Tracked in `docs/tickets/notification-event-tenant-id.md`:
 * once `notification.*` carries a real tenant, this sentinel goes away.
 */
export const SYSTEM_TENANT_ID = NIL_UUID

/**
 * Type-safe union of all event type values.
 * Use this when you need the string literal type instead of the enum.
 */
export type EventTypeValue = `${EventType}`

/**
 * Entity types for event + comment linking.
 *
 * Used in the `events` table (`entity_type` + `entity_id`) to link events
 * to specific domain entities. Enables indexed timeline queries like:
 * "every event for entity X".
 *
 * **Single source of truth** — every producer / consumer uses an enum
 * member, never a hand-typed string. CI's `lint:isolation` check enforces
 * this on primitives that must stay generic (per ADR-0011).
 *
 * Adding a new domain entity = one new line here. The audit reader picks
 * it up automatically.
 */
export enum EventEntityType {
	USER = 'user',
	CUSTOMER = 'customer',
	PRODUCT = 'product',
	// TO-415: a product category (its translations reference their product).
	PRODUCT_CATEGORY = 'product_category',
	OFFER = 'offer',
	TENANT = 'tenant',
	/** One inbound webhook delivery in the ledger (TO-323). */
	WEBHOOK = 'webhook',
	// TO-327: a prompt head (also the entity for its versions/labels/test cases/
	// eval runs — those reference their owning prompt) and a reusable variable list.
	PROMPT = 'prompt',
	PROMPT_VARIABLE_LIST = 'prompt_variable_list',
	FILE = 'file',
	SHORT_URL = 'short_url',
	ORG_UNIT = 'org_unit',
	ORGANIZATION_LOCATION = 'organization_location',
	/** A user ↔ organizational-unit assignment (TO-412). */
	ORG_ASSIGNMENT = 'org_assignment',
	// TO-419: content-service entities.
	CONTENT_TYPE = 'content_type',
	CONTENT_TEXT = 'content_text',
	CONTENT_LOCALIZATION = 'content_localization',
	CONTENT_ASSIGNMENT = 'content_assignment',
	/** One logical file configuration of a tenant (TO-391). */
	FILE_EXCHANGE_FILE = 'file_exchange_file',
	/** One import execution — one physical file of a tenant (TO-413). */
	FILE_EXCHANGE_IMPORT_EXECUTION = 'file_exchange_import_execution',
	/** One version of a tenant's import definition (TO-414). */
	FILE_EXCHANGE_IMPORT_DEFINITION_VERSION = 'file_exchange_import_definition_version',
	/** One missing scheduled file of a tenant: (logical file, due date) (TO-398). */
	FILE_EXCHANGE_MISSING_FILE_OCCURRENCE = 'file_exchange_missing_file_occurrence',
	/** One SFTP connector of a tenant (TO-393). */
	FILE_EXCHANGE_SFTP_CONNECTOR = 'file_exchange_sftp_connector',
	// TO-232: compliance-service entities. Compliance events link to the request
	// or hold, never to the customer, so they never count as the subject's own
	// audit rows.
	ERASURE_REQUEST = 'erasure_request',
	LEGAL_HOLD = 'legal_hold',
	SUBJECT_ACCESS_REQUEST = 'subject_access_request',
	/** One platform-wide setting, keyed by `PlatformSettingKey` (TO-541). */
	PLATFORM_SETTING = 'platform_setting',
}
